import React, {useEffect, useMemo, useState, useCallback} from "react"
import {ConfigProvider, Spin} from "antd"
import {MailCheck} from "lucide-react"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {AccountAPI, setToken, clearToken, resolveIdentifier} from "./api"
import {getLayoutDirection} from "../../locales"
import AuthScreen from "./AuthScreen"
import Dashboard from "./Dashboard"
import Profile from "./Profile"
import Family from "./Family"
import AccountShell from "./AccountShell"

// ─────────────────────────────────────────────────────────────────────
//  AccountSpace — composant partagé cross-thème (page-type "ACCOUNT").
//
//  Machine à états :
//    loading        → spinner (chargement /me au montage si token)
//    auth           → pas de token : connexion / inscription (email + OTP)
//    submit         → compte créé, en attente de validation par l'org
//    valide         → onglets Dashboard / Profil / Famille
//
//  Reçoit `context` (org, config, langValue…) comme tout composant de page.
//  Antd + Lingui + RTL (via ConfigProvider direction). Agnostique du thème :
//  ne dépend d'aucun Header/Footer spécifique — il s'insère dans le slot de page.
// ─────────────────────────────────────────────────────────────────────

export default function AccountSpace({context}) {
    const org = context?.org || {}
    const identifier = resolveIdentifier(org)
    const lang = context?.langValue || org?.language || "fr"
    const direction = getLayoutDirection(lang)

    // Couleur d'accent : thème 2026 expose des tokens ; sinon couleur org ; fallback bleu.
    const primary = useMemo(() => {
        return org?.theme?.primary || org?.web?.theme?.primary || context?.config?.theme?.primary || "#2B70A9"
    }, [org, context])

    const [phase, setPhase] = useState("loading")   // loading | auth | submit | valide
    const [account, setAccount] = useState(null)
    const [members, setMembers] = useState([])
    const [identityForm, setIdentityForm] = useState(null)  // form identité (même module que le register)
    const [meOrg, setMeOrg] = useState(null)                // org projetée par /me (devise/langue)
    const [features, setFeatures] = useState({allowFamily: true}) // flags config (masque les sections désactivées)
    const [tab, setTab] = useState("dashboard")      // dashboard | profile | family

    const loadMe = useCallback(async () => {
        // La session vit dans un cookie HttpOnly (non lisible en JS) partagé entre
        // sous-domaines. On ne peut donc PAS décider « connecté ? » depuis le JS :
        // on tente /me, et c'est le cookie (envoyé automatiquement au BFF) qui
        // tranche. Sans session, /me renvoie 403 → on retombe sur l'auth.
        try {
            const res = await AccountAPI.me()
            if (res?.success) {
                setAccount(res.account)
                setMembers(res.members || [])
                if (res.identityForm) setIdentityForm(res.identityForm)
                if (res.org) setMeOrg(res.org)
                if (res.features) setFeatures(res.features)
                setToken("1") // pose le hint « connecté » (prefill form/paiement rapide)
                setPhase(res.account?.state === "VALIDE" ? "valide" : "submit")
            } else {
                clearToken()
                setPhase("auth")
            }
        } catch (_) {
            clearToken()
            setPhase("auth")
        }
    }, [])

    useEffect(() => { loadMe() }, [loadMe])

    const onAuthenticated = useCallback((res) => {
        // res = {authenticated, account, state} (le token n'est PLUS renvoyé : il
        // est posé en cookie HttpOnly par le BFF). verify-code ne renvoie PAS les
        // membres → on recharge /me pour peupler `members` (sinon l'onglet Famille
        // serait vide au 1er login auto-validé).
        setAccount(res.account)
        setPhase(res.state === "VALIDE" ? "valide" : "submit")
        setTab("dashboard")
        if (res.state === "VALIDE") {
            loadMe()
        }
    }, [loadMe])

    const onLogout = useCallback(async () => {
        try { await AccountAPI.logout() } catch (_) {}
        clearToken()
        setAccount(null)
        setMembers([])
        setPhase("auth")
    }, [])

    let body
    if (phase === "loading") {
        body = (
            <div style={{textAlign: "center", padding: "80px 0"}}>
                <Spin size="large"/>
            </div>
        )
    } else if (phase === "auth") {
        body = <AuthScreen identifier={identifier} org={org} lang={lang} onAuthenticated={onAuthenticated}/>
    } else if (phase === "submit") {
        body = (
            <AccountShell org={org} account={account} onLogout={onLogout} tab={null} onTab={() => {}} hideTabs>
                <div style={{textAlign: "center", padding: "40px 0"}}>
                    <div style={{
                        width: 76, height: 76, borderRadius: "50%",
                        background: primary + "1A",   // teinte ~10% de la couleur d'accent
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        marginBottom: 16,
                    }}>
                        <MailCheck size={36} strokeWidth={1.75} color={primary}/>
                    </div>
                    <h2 style={{marginBottom: 8}}><Trans>Compte en attente de validation</Trans></h2>
                    <p style={{color: "#888", maxWidth: 440, margin: "0 auto"}}>
                        <Trans>
                            Votre compte a bien été créé. Il sera activé après vérification par {org?.name || t`l'organisation`}.
                            Vous pourrez ensuite accéder à votre tableau de bord, votre profil et votre famille.
                        </Trans>
                    </p>
                </div>
            </AccountShell>
        )
    } else {
        // valide. Si la gestion famille est désactivée mais qu'on est sur cet onglet
        // (ex. l'admin vient de la couper), on retombe sur le tableau de bord.
        const effectiveTab = (tab === "family" && !features.allowFamily) ? "dashboard" : tab
        body = (
            <AccountShell org={org} account={account} onLogout={onLogout} tab={effectiveTab} onTab={setTab} allowFamily={features.allowFamily}>
                {effectiveTab === "dashboard" && <Dashboard account={account} primary={primary} lang={lang}/>}
                {effectiveTab === "profile" && (
                    <Profile
                        account={account}
                        identityForm={identityForm}
                        org={meOrg || org}
                        lang={lang}
                        primary={primary}
                        onUpdated={setAccount}
                    />
                )}
                {effectiveTab === "family" && features.allowFamily && (
                    <Family
                        org={org}
                        account={account}
                        members={members}
                        lang={lang}
                        primary={primary}
                        onRefresh={loadMe}
                    />
                )}
            </AccountShell>
        )
    }

    return (
        <ConfigProvider direction={direction} theme={{token: {colorPrimary: primary}}}>
            <style dangerouslySetInnerHTML={{__html: ACS_CSS}}/>
            <div
                className="acs"
                style={{
                    minHeight: "60vh",
                    width: "min(900px, 100%)",
                    margin: "0 auto",
                    padding: "clamp(20px, 4vw, 32px) clamp(12px, 4vw, 16px) 64px",
                    boxSizing: "border-box",
                }}
            >
                {body}
            </div>
        </ConfigProvider>
    )
}

// Feuille de style responsive de l'espace compte (injectée une fois).
// Mobile-first : sur petit écran on COMPACTE (paddings/typos/icônes réduits, KPI en
// 2 colonnes, cartes denses avec montant à droite sur la même ligne) pour limiter le
// scroll. Onglets scrollables horizontalement.
const ACS_CSS = `
.acs { -webkit-text-size-adjust: 100%; }
.acs .ant-card { border-radius: 16px; }

/* ⚠️ NE PAS mettre overflow-x:auto sur .ant-segmented : ça en ferait un conteneur
   scrollable interne qui VOLE le scroll (centrage onglet) et entre en conflit avec
   <ScrollFade>. Le scroll des onglets est porté UNIQUEMENT par ScrollFade. */
.acs .ant-tabs-nav { margin-bottom: 8px; }

/* Le Segmented des onglets dashboard prend sa largeur NATURELLE (pas le mode block
   qui comprime les 4 items -> labels tronques). min-width:100% -> il remplit au moins
   le conteneur (desktop : tout tient), width:max-content -> il deborde quand les
   onglets ne tiennent pas (mobile) -> ScrollFade scrolle et CENTRE l'onglet actif.
   Regle NON conditionnelle au viewport : le debordement seul decide du scroll, donc
   ca marche aussi dans une colonne etroite sur grand ecran. */
.acs .acs-segmented-wrap .ant-segmented {
    display: inline-flex !important;
    min-width: 100%;
    width: max-content;
}
.acs .acs-segmented-wrap .ant-segmented .ant-segmented-group {
    width: max-content;
}
.acs .acs-segmented-wrap .ant-segmented .ant-segmented-item {
    flex: 0 0 auto;
}
.acs .acs-segmented-wrap .ant-segmented .ant-segmented-item-label {
    padding-inline: 14px;
    white-space: nowrap;
}

@media (max-width: 560px) {
    /* Bouton "Se déconnecter" : texte masqué → icône seule */
    .acs-logout-label { display: none; }
    .acs .ant-btn .anticon + span,
    .acs .ant-btn > .acs-logout-label { margin-inline-start: 0; }

    /* CARTES D'ITEM : compactes, montant/badge À DROITE sur la même ligne que le
       titre (le titre se tronque). On garde flex-wrap pour que SEUL le footer passe
       dessous ; le montant ne s'élargit plus à 100% (c'était ça qui doublait la hauteur). */
    .acs-card {
        gap: 11px !important;
        padding: 11px 13px !important;
        border-radius: 12px !important;
        margin-bottom: 8px !important;
    }
    .acs-card .acs-card-icon {
        width: 34px !important; height: 34px !important; border-radius: 9px !important;
    }
    .acs-card .acs-card-title { font-size: 14px !important; }
    .acs-card .acs-card-sub { font-size: 11.5px !important; }
    .acs-card .acs-card-right {
        width: auto !important;
        text-align: right !important;
        align-self: center;
        margin-top: 0 !important;
        padding-left: 0 !important;
    }
    .acs-card .acs-card-amount { font-size: 14px !important; }
    /* Le footer (boutons reçu / régler) reste pleine largeur sous la carte */
    .acs-card .acs-card-footer { flex-basis: 100% !important; margin-top: 9px !important; }

    /* KPI : 2 colonnes compactes (au lieu de tuiles pleine largeur empilées) */
    .acs-stats-grid {
        grid-template-columns: 1fr 1fr !important;
        gap: 9px !important;
    }
    .acs-stat { padding: 12px 13px !important; border-radius: 13px !important; }
    .acs-stat-icon { width: 30px !important; height: 30px !important; margin-bottom: 7px !important; }
    .acs-stat-value { font-size: 18px !important; }
    .acs-stat-label { font-size: 11.5px !important; }

    /* Salutation + panneaux (Profil/Famille) resserrés */
    .acs-greeting { margin-bottom: 12px !important; font-size: 18px !important; }
    .acs .acs-segmented-wrap { margin-top: 14px !important; margin-bottom: 14px !important; }
    .acs-panel { padding: 15px !important; border-radius: 14px !important; margin-bottom: 12px !important; }
}

/* Très petit écran : KPI encore plus denses */
@media (max-width: 360px) {
    .acs-stat-value { font-size: 16.5px !important; }
    .acs-card-title { font-size: 13.5px !important; }
}
`
